import React, { useEffect, useRef, useState } from "react";
import { Button, Loading, RadioGroup, Text, useNotifications } from "@ilc-technology/luik";
import { useQuoteContext } from "../../../contexts/QuoteContext/QuoteContext";
import Enrich from "../../../Common/services/TextEnricher";
import {
  ApiRequestType,
  ErrorCode,
  ErrorDetails,
  InitiateOraclePaymentRequest,
  InitiateOraclePaymentSessionResponse,
  Money,
  OraclePaymentLibraryResult,
  OraclePaymentResult,
  PaymentInfo,
  PaymentInfoStatus,
  PaymentOption,
  PaymentPlan,
  PaymentStatus,
  Quote,
  QuotePaymentStatus,
  ReservationRequest,
  ReservationResponse,
  stringNullable,
} from "../../../Common/Types";
import { LabelKey } from "../../../Common/StoryblokTypes";
import { useDatasources } from "../../../contexts/StoryblokContext/StoryblokContext";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSessionContext } from "../../../contexts/SessionContext/SessionContext";
import { FormatCurrency, FormattedMoney } from "../../../Common/Helpers/MoneyHelper";
import { AppConfig } from "../../../AppConfig";
import { GenerateHeaderInformation } from "../../../Common/Helpers/ApiHelper";
import {
  AdyenResultCodes,
  JWT_API_KEY,
  LATE_BOOKING_SPLIT_PAYMENT_ALLOWED_ABOVE_DAYS,
  RECURRING_PAYMENT_ALLOWED_ABOVE_DAYS,
  SPLIT_PAYMENT_ALLOWED_ABOVE_DAYS,
  SUPPORTED_PAYMENT_COUNTRY_CODES,
  TRUE,
} from "../../../Common/Constants";
import InformationBox from "../../InformationBox/InformationBox";
import StudentDetails from "./StudentDetails";
import { CustomTrackingEvent, trackEvent, trackPurchaseEvent } from "../../../Common/services/Analytics";
import { fetchRetry } from "../../../Common/services/FetchRetry";
import { generatePaymentSummaryUrl } from "../../../Common/generatePaymentSummaryUrl";
import {
  convertToErrorDetails,
  getErrorDetailsToDisplay,
  logError,
  returnJsonOrThrowError,
} from "../../../Common/services/ErrorService";
import { generatePriceQuoteEventItems } from "../../../Common/services/AnalyticsMappings";
import { PaymentDetailsResponse } from "../../../apis/quotationApi";
import { getQuoteInstallments } from "../../../apis/quoteInstallmentsClient";
import { QuoteInstallmentsResponse } from "../../../Common/Types";
import TBRadio from "../../BaseComponents/TBRadio";
import TermsAndConditions, { SalesCondition } from "./TermsAndConditions";
import { allTermsAccepted, shouldShowPayByBankWarning } from "./helpers/paymentHelpers";
import TBSection from "../../BaseComponents/TBSection";
import TBCardStatus, { TBCardStatusAlignment, TBCardStatusPosition } from "../../BaseComponents/TBCardStatus";
import { ColorVariant } from "../../../Common/services/ColorService";
import TitleValueCell from "../../BaseComponents/TitleValueCell";
import { calculateDateDifference, formatDate, formatDateRange } from "../../../Common/Helpers/DateHelper";
import NoTranslate from "../../BaseComponents/NoTranslate";
import InfoCard from "../../BaseComponents/InfoCard";
import PaymentCurrencySelector from "../../BaseComponents/PaymentCurrencySelector";
import ContentLayout from "../../BaseComponents/ContentLayout";
import { useLocalCurrencyPayment } from "./useLocalCurrencyPayment";
import {
  calculateTotalPrice,
  getDestinationName,
  getPaidInFullDiscount,
  getQuoteInformation,
} from "../../../Common/services/QuoteService";
import {
  createPaymentOptions,
  getPaymentOptionDescription,
  resolvePaymentType,
} from "../../../Common/Helpers/PaymentHelper";
import CustomAmount from "./CustomAmount";
import { InstallmentsDetails } from "./InstallmentsDetails";
import { useQuoteDetailsContext } from "../../../contexts/QuoteDetailsContext";
import { DiscountTitleValueCell } from "../../BaseComponents/DiscountTitleValueCell";
import { BasicStudentDetails } from "./StudentDetails";
import { Gender } from "../../../apis/generatedCustomerServiceApiClient";
import { normalizeProgramCode } from "../../../Common/normalizeProgramCode";
import ParentDetails from "./ParentDetails";
import moment from "moment/moment";

interface PaymentProps {
  quote: Quote;
  reservationPrice: Money;
}

const destroyAdyenDropin = () => {
  try {
    window.adyenDropin?.destroy();
  } catch {
    // Adyen may already have removed its nodes during a concurrent React update.
  }
  // @ts-expect-error - TODO: Fix typings
  window.adyenDropin = undefined;

  const paymentForm = document.getElementById("paymentForm");
  paymentForm?.replaceChildren();
};

const Payment: React.FC<PaymentProps> = ({ quote, reservationPrice }) => {
  const { pathname, search } = useLocation();
  const { quoteId } = useParams();
  const { session, language } = useSessionContext();
  const { quotesContentCache, activeLanguage, addPaymentToQuote } = useQuoteContext();
  const { paidFullDiscount, installments } = useQuoteDetailsContext();
  const { labels, featureSettings, programs } = useDatasources();
  const navigate = useNavigate();
  const notifications = useNotifications();

  const isFullPaymentEnabled = featureSettings.isFullPaymentEnabled === TRUE;

  const isPaymentEnabled =
    featureSettings.isPaymentEnabled === TRUE &&
    !(featureSettings.hidePaymentsForLT === TRUE && programs[normalizeProgramCode(quote.program)] === "lt");
  const isReservationEnabled = featureSettings.isReservationEnabled === TRUE;
  const showOnlyReservation = !isPaymentEnabled && isReservationEnabled;
  const isCustomPaymentEnabled = featureSettings.isCustomPaymentEnabled === TRUE;
  const isRecurringPaymentEnabled = featureSettings.isRecurringPaymentEnabled === TRUE;
  const isLocalCurrencyPaymentEnabled = featureSettings.isLocalCurrencyPaymentEnabled === TRUE;
  const isSponsoredOpportunityEnabled = featureSettings.isSponsoredOpportunityEnabled === TRUE;
  const shouldRequestEnglishName = featureSettings.shouldRequestEnglishName === TRUE;
  const isHidingThreeInstallmentAlt = featureSettings.isHidingThreeInstallmentAlt === TRUE;
  const isPaidInFullHiddenForAgents = featureSettings.hidePaidInFullForAgents === TRUE;
  const isLegacyPaymentPlanHiddenForMarket = featureSettings.hideLegacyPaymentPlan === TRUE;
  const isSplitPaymentHiddenWithin30Days = featureSettings.hideSplitPaymentWithin30Days === TRUE;
  const isParentDetailsEnabledPayment = featureSettings.isParentDetailsEnabledPayment === TRUE;

  const paymentChooser = useRef(null);
  const quoteInfo = getQuoteInformation(quote);
  const daysLeftUntilDeparture = calculateDateDifference(new Date(), new Date(quoteInfo.startAt));

  const [studentsCountryOfResidence, setStudentsCountryOfResidence] = useState<string>("");
  const [studentGender, setStudentGender] = useState<Gender>();
  const [studentDateOfBirth, setStudentDateOfBirth] = useState<string>("");
  const [studentEmail, setStudentEmail] = useState<string>("");
  const [studentFirstnameLatin, setStudentFirstnameLatin] = useState<stringNullable>();
  const [studentLastnameLatin, setStudentLastnameLatin] = useState<stringNullable>();
  const [studentNationality, setStudentNationality] = useState<string>("");

  const [acceptedTermsVersion, setAcceptedTermsVersion] = useState<string | undefined>(undefined);
  const [acceptedConditions, setAcceptedConditions] = useState(0);
  const [isLoadingPaymentSession, setIsLoadingPaymentSession] = useState<boolean | undefined>(undefined);
  const [isLoadingReservation, setIsLoadingReservation] = useState<boolean>(false);
  const [referenceId, setReferenceId] = useState<string | undefined>(undefined);
  const [destinationName, setDestinationName] = useState<string>("");
  const [amountConfirmed, setAmountConfirmed] = useState(true);
  const [showPaymentFailureError, setShowPaymentFailureError] = useState<ErrorDetails | undefined>(undefined);
  const [chosenPaymentOption, setChosenPaymentOption] = useState(PaymentOption.FullPayment);
  const [chosenPaymentPlan, setChosenPaymentPlan] = useState<PaymentPlan>(PaymentPlan.Monthly);
  const [quoteInstallmentsData, setQuoteInstallmentsData] = useState<QuoteInstallmentsResponse>();
  const [loadingQuoteInstallments, setLoadingQuoteInstallments] = useState(false);
  const [showThreeInstallmentsAlternative, setShowThreeInstallmentsAlternative] = useState(false);
  const [selectedPaymentMethodType, setSelectedPaymentMethodType] = useState<string | undefined>(undefined);
  const discountValue = getPaidInFullDiscount(paidFullDiscount?.price?.prices ?? [], installments?.payInFullDiscount);
  const payInFullDiscountAmount =
    !isRecurringPaymentEnabled || daysLeftUntilDeparture <= RECURRING_PAYMENT_ALLOWED_ABOVE_DAYS ? 0 : discountValue;
  const hasDiscount = !!payInFullDiscountAmount;
  const discountedPrice = { ...quote.prices.totalPrice };
  const DATE_FORMAT = "YYYY-MM-DD";
  const age = moment().diff(moment(studentDateOfBirth, DATE_FORMAT, true), "years");

  if (hasDiscount) {
    discountedPrice.amount -= Math.abs(payInFullDiscountAmount);
  }
  const formattedDiscountedPrice = FormattedMoney(discountedPrice);

  const amountForFullPayment = hasDiscount ? discountedPrice.amount : quote.prices.totalPrice.amount;

  const [paymentValue, setPaymentValue] = useState<Money>({
    amount: chosenPaymentOption == PaymentOption.FullPayment ? amountForFullPayment : reservationPrice.amount,
    currency: quote.prices.totalPrice.currency,
  });

  const localCurrency = useLocalCurrencyPayment({
    quoteId: quote.id,
    jwtAccessToken: session.jwtAccessToken,
    paymentValue,
    chosenPaymentOption,
    hasDiscount,
    discountedPrice,
    labels,
    isEnabled: isLocalCurrencyPaymentEnabled,
  });
  const formattedPayNowAmount =
    localCurrency.payInLocalCurrency && localCurrency.localAmountForCurrentSelection !== undefined
      ? localCurrency.formattedDisplayValue
      : chosenPaymentOption == PaymentOption.FullPayment && hasDiscount
        ? formattedDiscountedPrice
        : FormattedMoney(paymentValue);

  const currencySelectorElement = localCurrency.canOfferLocalCurrency && localCurrency.localPrice && (
    <PaymentCurrencySelector
      title={labels[LabelKey.paymentCurrencySelector_title]}
      baseCurrencyCode={paymentValue.currency}
      localCurrencyCode={localCurrency.localPrice.currency}
      isLocalSelected={localCurrency.payInLocalCurrency}
      onChange={localCurrency.setPayInLocalCurrency}
    />
  );

  const isWaitingForLocalPrice = isLocalCurrencyPaymentEnabled && localCurrency.isLoadingLocalPrice;

  const quoteStatus = quote.status;
  const normalizedProgramCode = normalizeProgramCode(quote.program);

  const { installments: installmentData } = installments || {};

  const hasInstallments = (data: typeof installmentData): data is Required<NonNullable<typeof installmentData>> => {
    return !!(
      data &&
      data.numberOfInstallments &&
      data.amount &&
      data.currencyCode &&
      data.firstInstallmentDueDate &&
      data.lastInstallmentDueDate
    );
  };

  const installmentDataComplete = hasInstallments(installmentData);
  const isEligibleForRecurringPayment = isRecurringPaymentEnabled && installmentDataComplete;

  const shouldRenderPaymentSection = isPaymentEnabled && quoteStatus !== QuotePaymentStatus.Paid && paymentValue;

  const areLatinDetailsProvidedOrNotRequired =
    !shouldRequestEnglishName || (!!studentFirstnameLatin?.trim() && !!studentLastnameLatin?.trim());

  const areMandatoryDetailsProvided =
    studentsCountryOfResidence != "" &&
    !!studentNationality &&
    !!studentGender &&
    studentDateOfBirth != "" &&
    !!studentEmail?.trim() &&
    areLatinDetailsProvidedOrNotRequired;

  const shouldDisplayPaymentOptions =
    areMandatoryDetailsProvided && isFullPaymentEnabled && allTermsAccepted(acceptedConditions);

  const shouldDisplayPaymentInfomrationLabel = shouldDisplayPaymentOptions;

  const isSplitLikeOption = chosenPaymentOption === PaymentOption.SplitPayment;
  const shouldDisplayPaymentPlans = isEligibleForRecurringPayment && isSplitLikeOption;
  const isSponsoredOpportunity = isSponsoredOpportunityEnabled && !!session?.opportunity?.isSponsored;
  // Agents are not offered paid in full (feature flag is enabled per market, e.g. South Korea)
  const isPaidInFullHidden = isPaidInFullHiddenForAgents && !!session?.opportunity?.isAgent;
  // Legacy payment plans are hidden per market, unless the opportunity is explicitly allowed to keep them
  const isLegacyPaymentPlanHidden =
    isLegacyPaymentPlanHiddenForMarket && !session?.opportunity?.customPaymentPlanEnabled;
  // Markets with the flag only offer split payment when departure is more than 30 days away (RLT-7024)
  const splitPaymentAllowedAboveDays = isSplitPaymentHiddenWithin30Days
    ? LATE_BOOKING_SPLIT_PAYMENT_ALLOWED_ABOVE_DAYS
    : SPLIT_PAYMENT_ALLOWED_ABOVE_DAYS;
  const { paymentOptions, paymentPlans } = createPaymentOptions({
    labels,
    reservationPrice,
    totalPrice: quote.prices.totalPrice,
    isRecurringPaymentEnabled: isEligibleForRecurringPayment,
    isCustomPaymentEnabled,
    isWithinRecurringPaymentWindow: daysLeftUntilDeparture > RECURRING_PAYMENT_ALLOWED_ABOVE_DAYS,
    isWithinSplitPaymentWindow: daysLeftUntilDeparture > splitPaymentAllowedAboveDays,
    isSponsoredOpportunity,
    isPaidInFullHidden,
    isLegacyPaymentPlanHidden,
  });

  // The legacy payment plan flag takes over: installments are either removed or shown directly, never behind a link
  const isInstallmentsBehindLink = isHidingThreeInstallmentAlt && !isLegacyPaymentPlanHiddenForMarket;
  const hasInstallmentsPlan = paymentPlans.some((p) => p.key === PaymentPlan.Installments);
  const visiblePaymentPlans =
    isInstallmentsBehindLink && !showThreeInstallmentsAlternative && hasInstallmentsPlan
      ? paymentPlans.filter((p) => p.key !== PaymentPlan.Installments)
      : paymentPlans;
  const showOtherWaysToPayLink = isInstallmentsBehindLink && hasInstallmentsPlan && !showThreeInstallmentsAlternative;

  const canShowPaymentElements =
    areMandatoryDetailsProvided &&
    allTermsAccepted(acceptedConditions) &&
    showPaymentFailureError === undefined &&
    referenceId !== undefined;

  const payByBankWarning = labels[LabelKey.payByBankDisclaimer];
  const showPayByBankWarning = shouldShowPayByBankWarning(selectedPaymentMethodType, payByBankWarning);

  const weeksLabel =
    quoteInfo.totalDuration === 1
      ? labels[LabelKey.durationWeek]
      : quoteInfo.totalDuration > 4
        ? labels[LabelKey.duration5orMoreWeeks]
        : labels[LabelKey.duration2to4Weeks];

  const onRadioChange = (option: PaymentOption) => {
    return () => {
      if (option == PaymentOption.CustomAmountPayment) {
        setAmountConfirmed(false);
      } else {
        setAmountConfirmed(true);
      }
      if (chosenPaymentOption == option) {
        return;
      }
      setPaymentValue({
        ...paymentValue,
        amount:
          option === PaymentOption.FullPayment || option === PaymentOption.FullPaymentWithDiscount
            ? calculateTotalPrice(quote, payInFullDiscountAmount)
            : reservationPrice.amount,
      });
      setReferenceId(undefined);
      setChosenPaymentOption(option);
    };
  };

  // Full payment is selected by default; move to the first available option when it is hidden
  useEffect(() => {
    if (!isPaidInFullHidden || chosenPaymentOption !== PaymentOption.FullPayment) return;
    const fallbackOption = paymentOptions.find((option) => option.key !== PaymentOption.FullPayment);
    if (fallbackOption) {
      onRadioChange(fallbackOption.key)();
    }
  }, [isPaidInFullHidden, chosenPaymentOption, paymentOptions.length]);

  useEffect(() => {
    if (!quote?.id || !shouldDisplayPaymentPlans) return;
    setLoadingQuoteInstallments(true);
    getQuoteInstallments(quote.id)
      .then((response) => {
        setQuoteInstallmentsData(response);
      })
      .catch((error) => {
        console.error("[Payment] getQuoteInstallments error", error);
        setQuoteInstallmentsData(undefined);
      })
      .finally(() => setLoadingQuoteInstallments(false));
  }, [quote?.id, shouldDisplayPaymentPlans]);

  useEffect(() => {
    if (quotesContentCache && quoteInfo.courseSegments.length != 0) {
      setDestinationName(getDestinationName(quoteInfo.courseSegments, quotesContentCache?.destinations));
    }
  }, [quotesContentCache, quoteInfo.courseSegments]);

  const redirectToPaymentSummary = (
    status: PaymentStatus,
    referenceId: string | undefined,
    paymentValue: Money | undefined
  ) => {
    const redirectUrl = generatePaymentSummaryUrl(
      status,
      chosenPaymentOption == PaymentOption.FullPayment ? discountValue : 0,
      referenceId,
      paymentValue,
      quoteId!,
      localCurrency.payInLocalCurrency && localCurrency.localPrice ? localCurrency.localPrice.currency : undefined
    );
    trackEvent(
      quote.opportunityUuid,
      chosenPaymentOption ? CustomTrackingEvent.FullPaymentChosen : CustomTrackingEvent.DownPaymentChosen
    );
    navigate(redirectUrl);
  };

  const sendReservationEvent = async () => {
    setIsLoadingReservation(true);
    fetchRetry(`${AppConfig.api.quotation}/reservations`, {
      method: "POST",
      headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
      body: JSON.stringify({
        quoteExternalId: quote.id,
        termsVersion: acceptedTermsVersion,
      } as ReservationRequest),
    })
      .then((response) => returnJsonOrThrowError<ReservationResponse>(response))
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      .then((_) => {
        trackEvent(quote.opportunityUuid, CustomTrackingEvent.Reserved);
        const payment: PaymentInfo = {
          payment: {
            amount: 0.0,
            currency: quote.prices.totalPrice.currency,
          },
          status: PaymentInfoStatus.Reserve,
          madeAt: new Date().toISOString(),
        };
        addPaymentToQuote(quote, payment);
        setIsLoadingReservation(false);
        redirectToPaymentSummary(PaymentStatus.ReservationSuccess, undefined, undefined);
      })
      .catch((error) => {
        setIsLoadingReservation(false);
        notifications.addErrorNotification({
          title: labels[LabelKey.paymentStatus_ReservationFailureHeader],
          description: labels[LabelKey.paymentStatus_ReservationFailureMessage],
        });
        logError(convertToErrorDetails(error, ErrorCode.ReservationsPostFailed));
      });
  };

  const initiateOraclePaymentSession = () => {
    if (isLoadingPaymentSession) {
      return;
    }
    const normalizedCountryCode = studentsCountryOfResidence?.trim().toUpperCase();
    if (!SUPPORTED_PAYMENT_COUNTRY_CODES.has(normalizedCountryCode)) {
      notifications.addErrorNotification({
        title: labels[LabelKey.paymentStatus_PaymentFailureHeader],
        description: `Selected country (${normalizedCountryCode}) is not supported for online payments. Please choose another country in the Student Details section and try again.`,
      });
      return;
    }

    const paymentType = resolvePaymentType(chosenPaymentOption, chosenPaymentPlan, hasDiscount);

    const request: InitiateOraclePaymentRequest = {
      externalId: quote.id,
      termsVersion: acceptedTermsVersion!,
      returnUrl: `${window.location.origin}/payment/` + quote.id,
      countryCode: normalizedCountryCode,
      paymentSource: "Trip Booker",
      efComPaymentSource: "MyEFPlanner-Adyen",
      // payment reference
      paymentReferenceAction: "Pay for quote",
      requestedPaymentAmount: paymentValue.amount,
      locale: language,
      paymentType: paymentType,
      ...(localCurrency.payInLocalCurrency &&
      localCurrency.localPrice &&
      localCurrency.localAmountForCurrentSelection !== undefined
        ? {
            localCurrency: localCurrency.localPrice.currency,
            localAmount: localCurrency.localAmountForCurrentSelection,
            exchangeRate: localCurrency.localPrice.appliedExchangeRate,
          }
        : {}),
    };

    setIsLoadingPaymentSession(true);
    setShowPaymentFailureError(undefined);
    setSelectedPaymentMethodType(undefined);
    fetchRetry(`${AppConfig.api.quotation}/payments/initiate`, {
      method: "POST",
      redirect: "manual",
      headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
      body: JSON.stringify(request),
    })
      .then((response) => returnJsonOrThrowError<InitiateOraclePaymentSessionResponse>(response))
      .then((res) => {
        if (!document.getElementById("paymentForm")) {
          return;
        }
        if (!res.correlationId) {
          const errorDetails = {
            code: ErrorCode.InitPaymentSessionFailed,
            details: {
              additionalDetails: JSON.stringify(res),
            },
          };
          setIsLoadingPaymentSession(false);
          setShowPaymentFailureError(errorDetails);
          logError(errorDetails);
          return;
        }

        setReferenceId(res.correlationId);
        destroyAdyenDropin();

        const dependencies = {
          countryCode: studentsCountryOfResidence,
          requestedPaymentAmount: paymentValue.amount,
          locale: language,
          isLoadingPaymentSession,
          isLoadingReservation,
          referenceId,
          destinationName,
          chosenPaymentOption,
          chosenPaymentPlan,
          discountValue,
          areMandatoryDetailsProvided,
        };

        window.adyenDropin = new window.EF.AdyenDropin();
        window.adyenDropin.startSessionsFlow({
          containerId: "#paymentForm",
          payByBankName: labels[LabelKey.payByBank],
          sessionId: res.sessionId,
          sessionData: res.sessionData,
          correlationId: res.correlationId,
          cardName: labels[LabelKey.creditOrDebitCard],
          onReady: function () {
            window.setTimeout(() => setIsLoadingPaymentSession(false), 0);
          },
          onSelect: function (activeComponent: AdyenDropinComponent) {
            setSelectedPaymentMethodType(activeComponent?.type ?? activeComponent?.props?.type);
          },
          onPaymentCompleted: function (result: OraclePaymentLibraryResult) {
            const payment: PaymentInfo = {
              payment: paymentValue,
              status: PaymentInfoStatus.Paid,
              madeAt: new Date().toISOString(),
            };
            if (result.resultCode === AdyenResultCodes.Authorised) {
              trackPurchaseEvent(
                res.merchantReference,
                quote.opportunityUuid,
                generatePriceQuoteEventItems(quote),
                paymentValue
              );
              addPaymentToQuote(quote, payment);
              redirectToPaymentSummary(PaymentStatus.PaymentSuccess, res.correlationId, paymentValue);
            } else if (
              result.resultCode === AdyenResultCodes.Pending ||
              result.resultCode === AdyenResultCodes.Received
            ) {
              trackPurchaseEvent(
                res.merchantReference,
                quote.opportunityUuid,
                generatePriceQuoteEventItems(quote),
                paymentValue
              );
              addPaymentToQuote(quote, payment);
              redirectToPaymentSummary(PaymentStatus.PaymentProcessed, res.correlationId, paymentValue);
            } else if (isError(result.resultCode)) {
              const details: OraclePaymentResult = {
                resultCode: result.resultCode,
                sessionId: res.sessionId,
                correlationId: res.correlationId,
              };
              const errorDetails: ErrorDetails = {
                code: ErrorCode.PaymentFailed,
                details: {
                  additionalDetails: JSON.stringify(details),
                },
              };

              setShowPaymentFailureError(errorDetails);

              if (details.resultCode === AdyenResultCodes.Refused) {
                // "Payment refused" is an error that we should not include in logs.
                return;
              }

              logError(errorDetails);
            }
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onError: function (error: any) {
            window.setTimeout(() => setIsLoadingPaymentSession(false), 0);
            const errorDetails: ErrorDetails = {
              code: ErrorCode.PaymentGatewayReturnedError,
              details: {
                additionalDetails: `Failed to initialize Adyen session: ${error.message} ${JSON.stringify(error?.cause ?? "")} | Dependencies: ${JSON.stringify(dependencies)}`,
              },
            };
            logError(errorDetails);
            setShowPaymentFailureError(errorDetails);
          },
        });
      })
      .catch((error) => {
        setIsLoadingPaymentSession(false);
        const errorDetails = convertToErrorDetails(error, ErrorCode.InitPaymentSessionFailed);
        setShowPaymentFailureError(errorDetails);
        logError(convertToErrorDetails(error, ErrorCode.OracleSessionInitFailed));
      });
  };

  const scrollToElement = (myElementRef: React.RefObject<Element>) => {
    if (myElementRef?.current) {
      myElementRef?.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  };

  const isError = (result: string) => {
    return (
      result === AdyenResultCodes.Error || result === AdyenResultCodes.Cancelled || result === AdyenResultCodes.Refused
    );
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (
      (acceptedConditions & SalesCondition.TermsAccepted) > 0 &&
      (acceptedConditions & SalesCondition.PrivacyPolicyAccepted) > 0 &&
      areMandatoryDetailsProvided &&
      quote !== undefined &&
      session.user &&
      amountConfirmed
    ) {
      initiateOraclePaymentSession();
      scrollToElement(paymentChooser);
    }
  }, [
    paymentValue,
    acceptedConditions,
    quote,
    areMandatoryDetailsProvided,
    amountConfirmed,
    chosenPaymentPlan,
    localCurrency.payInLocalCurrency,
  ]);

  useEffect(() => {
    if (search) {
      const qs = new URLSearchParams(search);
      const correlationId = qs.get("correlationId");
      const sessionId = qs.get("sessionId");
      const redirectResult = qs.get("redirectResult");
      const merchantReference = qs.get("merchantReference");

      if (sessionId && redirectResult && correlationId && window.EF?.AdyenDropin) {
        const adyenDropin = new window.EF.AdyenDropin();

        fetchRetry(`${AppConfig.api.quotation}/payments?correlationId=${correlationId}`, {
          method: ApiRequestType.GET,
          headers: GenerateHeaderInformation(session.jwtAccessToken, JWT_API_KEY),
        })
          .then((response) => returnJsonOrThrowError<PaymentDetailsResponse>(response))
          .then((res) => {
            if (res) {
              // @ts-expect-error TODO: Add typings
              adyenDropin.submitRedirectResult({
                sessionId: encodeURIComponent(sessionId),
                redirectResult: encodeURIComponent(redirectResult),

                onPaymentCompleted: function (result: OraclePaymentLibraryResult) {
                  const value = { amount: res.amount, currency: res.currency };

                  const payment: PaymentInfo = {
                    payment: value,
                    status: PaymentInfoStatus.Paid,
                    madeAt: new Date().toISOString(),
                  };
                  switch (result.resultCode) {
                    case AdyenResultCodes.Authorised:
                      if (paymentValue && merchantReference) {
                        trackPurchaseEvent(
                          merchantReference,
                          quote.opportunityUuid,
                          generatePriceQuoteEventItems(quote),
                          res
                        );
                      }
                      addPaymentToQuote(quote, payment);
                      redirectToPaymentSummary(PaymentStatus.PaymentSuccess, correlationId, value);
                      break;
                    case AdyenResultCodes.Pending:
                    case AdyenResultCodes.Received:
                    case AdyenResultCodes.PresentToShopper:
                      if (paymentValue && merchantReference) {
                        trackPurchaseEvent(
                          merchantReference,
                          quote.opportunityUuid,
                          generatePriceQuoteEventItems(quote),
                          res
                        );
                      }
                      addPaymentToQuote(quote, payment);
                      redirectToPaymentSummary(PaymentStatus.PaymentProcessed, correlationId, value);
                      break;
                    case AdyenResultCodes.Error:
                    case AdyenResultCodes.Refused:
                    case AdyenResultCodes.Cancelled:
                      navigate(pathname);
                      setShowPaymentFailureError({
                        code: ErrorCode.ReceivingPaymentsWasCancelled,
                        details: {
                          additionalDetails: `Payment correlation id: ${correlationId}`,
                        },
                      });
                      break;
                  }
                },
                onPaymentFailed: function (result: OraclePaymentLibraryResult) {
                  navigate(pathname);
                  const errorDetails: ErrorDetails = {
                    code: ErrorCode.ReceivingPaymentsError,
                    details: {
                      additionalDetails: `Payment with the following correlation id failed: ${correlationId} | Details: ${JSON.stringify(result)}`,
                    },
                  };
                  setShowPaymentFailureError(errorDetails);
                },
                onError: function () {
                  navigate(pathname);
                  const errorDetails: ErrorDetails = {
                    code: ErrorCode.ReceivingPaymentsError,
                  };
                  setShowPaymentFailureError(errorDetails);
                },
              });
            }
          })
          .catch((error) => {
            logError(
              convertToErrorDetails(error, ErrorCode.PaymentsWithCorrelationFailedToFetch, {
                additionalDetails: `Payment correlation id: ${correlationId}`,
              })
            );
            if (!showPaymentFailureError) {
              setShowPaymentFailureError({
                code: ErrorCode.ReceivingPaymentsError,
              });
            }
          });
      }
    }
  }, [search, window.EF?.AdyenDropin]);

  const handleAcceptanceChange = (acceptedCondition: SalesCondition, termsVersion: string) => {
    let event = CustomTrackingEvent.PrivacyPolicyAccepted;
    if (acceptedConditions === SalesCondition.TermsAccepted) {
      event = CustomTrackingEvent.TermsAndConditionsAccepted;
    }
    setAcceptedTermsVersion(termsVersion);
    trackEvent(quote.opportunityUuid, event);
    const updatedConditions = acceptedConditions ^ acceptedCondition; // Toggle the bit
    setAcceptedConditions(updatedConditions);
    if (showPaymentFailureError) {
      setReferenceId(undefined);
      setShowPaymentFailureError(undefined);
    }
  };

  const handleStudentDetailsChange = ({
    email,
    country,
    nationality,
    gender,
    dateOfBirth,
    lastNameLatin,
    firstNameLatin,
  }: BasicStudentDetails) => {
    setStudentEmail(email);
    setStudentsCountryOfResidence(country);
    setStudentGender(gender as Gender);
    setStudentDateOfBirth(dateOfBirth);
    setStudentNationality(nationality);
    setStudentFirstnameLatin(firstNameLatin);
    setStudentLastnameLatin(lastNameLatin);
  };

  return (
    <ContentLayout
      testId="payment-page"
      fullSizePanelShownFirst={true}
      topStaticCard={
        <div className="pt-6 lg:pt-8">
          <Text variant="heading-3-bold">{labels[LabelKey.completeBooking]}</Text>
        </div>
      }
      sidePanel={
        isWaitingForLocalPrice ? (
          <div className="m-auto flex justify-center overflow-hidden py-10" data-testid="payment-summary-card-loading">
            <Loading className="loading overflow-hidden rounded-full" size="lg" intent="black" />
          </div>
        ) : (
          <InfoCard
            summary={
              <>
                <NoTranslate>{formatDateRange(quoteInfo.startAt, quoteInfo.endAt, language)}</NoTranslate>
                {` | ${Enrich(weeksLabel, { numWeeks: quoteInfo.totalDuration })}`}
              </>
            }
            destinationName={destinationName}
            contentText={quoteInfo.courseInfo}
            currencySelector={currencySelectorElement}
            priceFooterElement={
              <>
                {chosenPaymentOption == PaymentOption.FullPayment && hasDiscount && (
                  <DiscountTitleValueCell
                    title={labels[LabelKey.payNowDiscount]}
                    discount={{ amount: payInFullDiscountAmount, currency: quote.prices.totalPrice.currency }}
                  />
                )}
                {localCurrency.canOfferLocalCurrency && (
                  <Text variant="label-sm" className="text-gray-45" data-testid="payment-currency-disclaimer">
                    {localCurrency.disclaimerText}
                  </Text>
                )}
              </>
            }
            amountLabel={labels[LabelKey.total]}
            amount={localCurrency.totalDisplayValue}
            bottomChildren={
              allTermsAccepted(acceptedConditions) ? (
                <TBCardStatus
                  variant={ColorVariant.Green}
                  alignment={TBCardStatusAlignment.Spread}
                  position={TBCardStatusPosition.UnderCard}
                >
                  <TitleValueCell
                    leftTextValue={labels[LabelKey.payNow]}
                    rightTextValue={formattedPayNowAmount}
                    testId="payment-summary-to-pay"
                  />
                </TBCardStatus>
              ) : null
            }
          />
        )
      }
    >
      <div className="a-gap flex w-full flex-col">
        <StudentDetails
          onStudentDetailsChange={handleStudentDetailsChange}
          hasFlightArticle={quoteInfo.hasFlightArticle}
        />
        {isParentDetailsEnabledPayment && age < 18 && <ParentDetails />}

        {activeLanguage && (
          <TBSection title={labels[LabelKey.bookingConditions]}>
            <TermsAndConditions
              activeLanguage={activeLanguage}
              market={quote.market}
              program={normalizedProgramCode}
              onConditionsAcceptanceChange={handleAcceptanceChange}
            />
          </TBSection>
        )}
        {shouldRenderPaymentSection && (
          <>
            <TBSection title={labels[LabelKey.payment]} ref={paymentChooser}>
              {shouldDisplayPaymentInfomrationLabel && (
                <Text variant="label-xl-bold">{labels[LabelKey.paymentInformation]}</Text>
              )}
              {shouldDisplayPaymentOptions && (
                <div data-testid="payment-options">
                  <RadioGroup
                    aria-label="payment-options"
                    className="a-radio-group"
                    containerClassName="w-full"
                    value={chosenPaymentOption}
                    defaultValue={PaymentOption.FullPayment}
                  >
                    {paymentOptions.map((option) => {
                      return (
                        <TBRadio
                          key={option.key}
                          value={option.key}
                          title={option.title}
                          description={getPaymentOptionDescription({
                            option: option.key,
                            quote,
                            reservationPrice,
                            paymentValue,
                            amountConfirmed,
                            chosenPaymentOption,
                            payInFullDiscountAmount,
                            localAmounts: localCurrency.localAmounts,
                          })}
                          isDisabled={isLoadingPaymentSession === true}
                          isSelected={chosenPaymentOption == option.key}
                          onClick={onRadioChange(option.key)}
                          testIdPrefix={option.key.toLowerCase()}
                        >
                          <Text variant="label-sm" className="text-gray-45">
                            {option.subtitle}
                          </Text>
                          {option.key === PaymentOption.CustomAmountPayment && (
                            <CustomAmount
                              minAmount={reservationPrice.amount}
                              maxAmount={quote.prices?.totalPrice.amount}
                              placeHolderText={labels[LabelKey.amount]}
                              buttonTitle={labels[LabelKey.confirm]}
                              valueOutOfRangeText={labels[LabelKey.paymentAmountOutOfRange]}
                              isDisabled={isLoadingPaymentSession === true}
                              isAmountConfirmed={amountConfirmed}
                              isVisible={chosenPaymentOption == PaymentOption.CustomAmountPayment}
                              onCustomAmountConfirmed={(values) => {
                                setPaymentValue({
                                  ...paymentValue,
                                  amount: Number(values.amount),
                                });
                                setAmountConfirmed(true);
                              }}
                            />
                          )}
                        </TBRadio>
                      );
                    })}
                  </RadioGroup>
                </div>
              )}
              {studentsCountryOfResidence == "" && (
                <InformationBox
                  data-testid="check-tandc-warning"
                  intent="warning"
                  title={labels[LabelKey.studentDetails]}
                  iconName={"alert-circle-outlined"}
                  testid="student-warning-info-box"
                />
              )}
              {(acceptedConditions & SalesCondition.TermsAccepted) <= 0 && (
                <InformationBox
                  data-testid="check-tandc-warning"
                  intent="warning"
                  title={labels[LabelKey.missingTerms]}
                  iconName={"alert-circle-outlined"}
                  testid="terms-warning-info-box"
                />
              )}
              {(acceptedConditions & SalesCondition.PrivacyPolicyAccepted) <= 0 && (
                <InformationBox
                  data-testid="check-tandc-warning"
                  intent="warning"
                  title={labels[LabelKey.missingPrivacyPolicy]}
                  iconName={"alert-circle-outlined"}
                  testid="privacy-warning-info-box"
                />
              )}
              {!studentGender && (
                <InformationBox
                  data-testid="check-tandc-warning"
                  intent="warning"
                  title={labels[LabelKey.missingGender]}
                  iconName={"alert-circle-outlined"}
                  testid="privacy-warning-info-box"
                />
              )}
              {!studentNationality && (
                <InformationBox
                  data-testid="check-tandc-warning"
                  intent="warning"
                  title={labels[LabelKey.missingNationality]}
                  iconName={"alert-circle-outlined"}
                />
              )}
              {!studentDateOfBirth && (
                <InformationBox
                  data-testid="check-tandc-warning"
                  intent="warning"
                  title={labels[LabelKey.missingDateOfBirth]}
                  iconName={"alert-circle-outlined"}
                  testid="privacy-warning-info-box"
                />
              )}
              {!studentEmail?.trim() && (
                <InformationBox
                  data-testid="check-tandc-warning"
                  intent="warning"
                  title={labels[LabelKey.missingEmail]}
                  iconName={"alert-circle-outlined"}
                  testid="email-warning-info-box"
                />
              )}
              {!areLatinDetailsProvidedOrNotRequired && (
                <InformationBox
                  data-testid="check-tandc-warning"
                  intent="warning"
                  title={labels[LabelKey.missingLatinDetails]}
                  iconName={"alert-circle-outlined"}
                  testid="privacy-warning-info-box"
                />
              )}
            </TBSection>
            {shouldDisplayPaymentPlans && (
              <TBSection title={labels[LabelKey.paymentPlan]}>
                <div data-testid="payment-plans">
                  <RadioGroup
                    aria-label="payment-plans"
                    className="a-radio-group"
                    containerClassName="w-full"
                    value={chosenPaymentPlan}
                    defaultValue={PaymentPlan.Monthly}
                  >
                    {visiblePaymentPlans.map((option) => {
                      return (
                        <TBRadio
                          key={option.key}
                          value={option.key}
                          title={option.title}
                          description={
                            option.key === PaymentPlan.Monthly ? (
                              <Text
                                variant="label-sm"
                                className="rounded-md bg-primary-accessible/15 p-1 text-primary-accessible/85"
                              >
                                {labels[LabelKey.mostPopular]}
                              </Text>
                            ) : null
                          }
                          isDisabled={isLoadingPaymentSession === true}
                          isSelected={chosenPaymentPlan == option.key}
                          onClick={() => setChosenPaymentPlan(option.key)}
                          testIdPrefix={option.key.toLowerCase()}
                          {...(option.key === PaymentPlan.Monthly && {
                            subtitleIcon: "refresh",
                            subTitle: labels[LabelKey.recurring],
                          })}
                        >
                          <Text variant="label-sm" className="text-gray-45">
                            {option.key === PaymentPlan.Deferred
                              ? option.subtitle
                              : installmentDataComplete && (
                                  <NoTranslate>
                                    {Enrich(option.subtitle, {
                                      numberOfPayments: installmentData.numberOfInstallments!,
                                      amountOfPaymentsWithCurrency: FormatCurrency(
                                        parseFloat(installmentData.amount!),
                                        installmentData.currencyCode!,
                                        2
                                      ),
                                      startDate: formatDate(installmentData.firstInstallmentDueDate!, language),
                                      endDate: formatDate(installmentData.lastInstallmentDueDate!, language),
                                    })}
                                  </NoTranslate>
                                )}
                          </Text>
                          {option.key === PaymentPlan.Monthly && (
                            <InstallmentsDetails
                              quoteInstallments={quoteInstallmentsData}
                              loading={loadingQuoteInstallments}
                              language={language}
                              labels={labels}
                              localCurrencyDisclaimer={localCurrency.installmentsDisclaimerText}
                            />
                          )}
                        </TBRadio>
                      );
                    })}
                  </RadioGroup>
                  {showOtherWaysToPayLink && (
                    <button
                      type="button"
                      onClick={() => setShowThreeInstallmentsAlternative(true)}
                      className="mt-3 text-left text-base font-normal not-italic text-[#6B6B6B] underline hover:underline focus:outline-none focus:ring-2 focus:ring-[#6B6B6B] focus:ring-offset-2"
                      data-testid="other-ways-to-pay"
                    >
                      {labels[LabelKey.showThreeInstallmentsAlternative]}
                    </button>
                  )}
                </div>
              </TBSection>
            )}
            {/* IMPORTANT: we hide it via css instead of js condition, since #paymentForm must be in the DOM to initialize and get populated by Adyen */}
            <div
              className={
                canShowPaymentElements || isLoadingPaymentSession || showPaymentFailureError ? "block" : "hidden"
              }
            >
              <TBSection title={labels[LabelKey.paymentMethod]}>
                <div
                  className={`py-14 ${isLoadingPaymentSession ? "" : "hidden"}`}
                  aria-hidden={!isLoadingPaymentSession}
                >
                  <Loading className="loading" size="lg" intent="black" />
                </div>

                <div
                  id="paymentForm"
                  data-testid="payment-form"
                  className={referenceId && !isLoadingPaymentSession ? "block" : "hidden"}
                />
                {canShowPaymentElements && !isLoadingPaymentSession && showPayByBankWarning && (
                  <div className="pt-4">
                    <InformationBox
                      intent="default"
                      title={payByBankWarning}
                      titleVariant="label-sm"
                      iconName={"info-circle-outlined"}
                      testid="pay-by-bank-warning-info-box"
                    />
                  </div>
                )}
                {!isLoadingPaymentSession && showPaymentFailureError && (
                  <InformationBox
                    testid="payment-error-info-box"
                    intent="alert"
                    title={labels[LabelKey.paymentStatus_PaymentFailureHeader]}
                    content={labels[LabelKey.paymentStatus_PaymentFailureMessage]}
                    iconName={"alert-circle-outlined"}
                    additionalInfo={getErrorDetailsToDisplay(showPaymentFailureError)}
                  />
                )}
              </TBSection>
              {canShowPaymentElements && isLoadingPaymentSession === false && (
                <div className="p-8">
                  <Text variant="label-sm" className="text-gray-40">
                    {Enrich(labels[LabelKey.finePrintPaymentText], { nameOfMerchant: "EF Education First" })}
                  </Text>
                </div>
              )}
            </div>
          </>
        )}
        {(isReservationEnabled || showOnlyReservation) &&
          quoteStatus === QuotePaymentStatus.Unreserved &&
          allTermsAccepted(acceptedConditions) && (
            <div className="mx-auto">
              <Button
                aria-label="reservation-button"
                data-testid="reservation-button"
                iconName="locked"
                iconPosition="start"
                intent="secondary-black"
                size="base"
                isLoading={isLoadingReservation}
                onPress={() => sendReservationEvent()}
                isDisabled={(acceptedConditions & SalesCondition.TermsAccepted) <= 0}
              >
                {labels[LabelKey.reservationFor24Hours]}
              </Button>
            </div>
          )}
      </div>
    </ContentLayout>
  );
};

export default Payment;
